import { useEffect } from 'react';
import { withAsyncBoundary } from '@toss/async-boundary';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { ZEM_CHARGE_POPUP_MESSAGE_TYPE, ZEM_CHARGE_POPUP_OPENER_ORIGIN_STORAGE_KEY, type ZemChargePopupMessage } from '@zep/common/routes';
import { logger } from '@zep/core-logger';
import { useNextRouter } from '@zep/next';
import { useViewportDevice } from '@zep/react';
import { Colors } from '@zep/ui-tokens';
import { fDateByLanguage } from '@zep/utils';
import { TossPaymentMethods } from '../../../../consts';
import { useChargeZemResult } from '../../../../services';
import { fCurrency } from '../../../../utils';
import { ChargeContainer, ChargeZemCompleteFooter } from './components/ChargeContainer';
import { ChargeZemIcon } from './components/PaymentSelectStep';
import S from './ChargeZemComplete.module.scss';
const getZemChargePopupTargetOrigin = () => {
  return window.sessionStorage.getItem(ZEM_CHARGE_POPUP_OPENER_ORIGIN_STORAGE_KEY) ?? window.location.origin;
};
const postZemChargeCompleteMessage = (orderId: string) => {
  if (!window.opener) {
    return;
  }
  const message: ZemChargePopupMessage = {
    type: ZEM_CHARGE_POPUP_MESSAGE_TYPE.COMPLETE,
    orderId
  };
  window.opener.postMessage(message, getZemChargePopupTargetOrigin());
};
export const ChargeZemComplete = () => {
  const {
    t
  } = useTranslation(['manage']);
  const onClose = () => {
    window.close();
  };
  return <ChargeContainer title={t('manage:charge.complete.title')} content={<Content />} mainAction={onClose} footer={<ChargeZemCompleteFooter action={onClose} />} data-sentry-element="ChargeContainer" data-sentry-component="ChargeZemComplete" data-sentry-source-file="ChargeZemComplete.tsx" />;
};
const ContentBody = () => {
  const {
    t,
    i18n: {
      resolvedLanguage
    }
  } = useTranslation(['manage']);
  const {
    user
  } = useAuth();
  const {
    isMobile
  } = useViewportDevice();
  const {
    query
  } = useNextRouter({
    suspense: true
  });
  const {
    data: {
      data
    }
  } = useChargeZemResult(String(query.orderId));
  const paymentMethod = TossPaymentMethods.find(({
    key
  }) => key === data.paymentMethod) || TossPaymentMethods[0];
  useEffect(() => {
    if (!data) return;
    postZemChargeCompleteMessage(data.orderId);
    logger.track('charge_zem_complete', {
      amount: data.productQuantity,
      currency: data.currency,
      method: paymentMethod.method,
      $revenue: Number(data.productQuantity) * 100
    });
  }, [data, paymentMethod.method]);
  return <div className={S.container} data-sentry-component="ContentBody" data-sentry-source-file="ChargeZemComplete.tsx">
      <div className={S.image_wrapper}>
        <img src="/images/zems/charge-zem-complete-image.png" alt="charge-complete" width={100} height={80} />
      </div>
      <div className={S.section_1}>
        <div className={S.body_1_sb_gray_700}>
          ZEP{' '}
          {t('manage:charge.complete.nameMessage', {
          name: user?.name ?? ''
        })}
        </div>
        <div>
          <span className={S.body_1_sb_gray_700}>
            {t('manage:charge.complete.orderConfirmNumber')}{' '}
          </span>
          <span className={S.body_1_sb_primary_default}>{data.orderId}</span>
        </div>
      </div>
      <Divider data-sentry-element="Divider" data-sentry-source-file="ChargeZemComplete.tsx" />
      <div className={S.section_2} style={{
      gap: isMobile ? 12 : undefined
    }}>
        <div className={S.sub_title}>
          {t('manage:charge.complete.paymentDetail')}
        </div>
        <div className={!isMobile ? S.section_2_row : S.section_2_col}>
          <div className={S.section_2_label}>
            <span className={S.body_1_m_gray_600}>
              {t('manage:charge.complete.productName')}
            </span>
            <div>
              <ChargeZemIcon data-sentry-element="ChargeZemIcon" data-sentry-source-file="ChargeZemComplete.tsx" />
              <span className={S.bold_gray}>{data.productQuantity} ZEM</span>
            </div>
          </div>
          <div className={S.section_2_label}>
            <span className={S.body_1_m_gray_600}>
              {t('manage:charge.complete.productPrice')}
            </span>
            <span className={S.bold_primary}>
              {fCurrency(data.amount, data.currency)}
            </span>
          </div>
        </div>
      </div>
      <Divider data-sentry-element="Divider" data-sentry-source-file="ChargeZemComplete.tsx" />

      <div className={S.section_2}>
        <div className={S.sub_title}>
          {t('manage:charge.complete.paymentMethod')}
        </div>
        <div className={S.body_1_m_gray_600}>
          {t(paymentMethod.labelKey)} |{' '}
          {fDateByLanguage(data.purchasedAt,
        // @ts-ignore
        resolvedLanguage)}
        </div>
      </div>
    </div>;
};
const Content = withAsyncBoundary(ContentBody, {
  pendingFallback: '',
  rejectedFallback: () => {
    return <>Error Occur</>;
  }
});
const Divider = () => {
  return <div style={{
    width: '100%',
    height: '1px',
    backgroundColor: Colors.Gray300
  }} data-sentry-component="Divider" data-sentry-source-file="ChargeZemComplete.tsx" />;
};